import { CircleHelp } from "lucide-react";
import { type JSX, useId, useState } from "react";
import { cx } from "../cx";
import { IconButton } from "./IconButton";

export interface GateHintProps {
  /** What the hint explains, e.g. "Upload blob" — names the toggle. */
  action: string;
  /** Backend-delivered human-readable reason the action is unavailable. */
  reason: string;
  /** Backend diagnostic code, rendered mono inside the note. */
  code?: string | undefined;
  className?: string | undefined;
}

/**
 * On-demand explanation for a backend-gated (disabled) control: a small
 * help affordance that reveals the backend's reason and diagnostic code.
 * Replaces standing inline gate paragraphs — the surface stays quiet
 * while the explanation stays one deliberate interaction away.
 */
export function GateHint({ action, reason, code, className }: GateHintProps): JSX.Element {
  const noteId = useId();
  const [open, setOpen] = useState(false);

  return (
    <span className={cx("tk-gate-hint", className)}>
      <IconButton
        label={`Why is ${action} unavailable?`}
        size="sm"
        aria-expanded={open}
        aria-controls={noteId}
        className={cx("tk-gate-hint__toggle", open && "is-open")}
        onClick={() => setOpen((current) => !current)}
      >
        <CircleHelp size={14} aria-hidden="true" />
      </IconButton>
      <span id={noteId} hidden={!open}>
        {open ? (
          <span className="tk-gate-hint__note" role="note">
            {reason}
            {code === undefined ? null : <code className="tk-gate-hint__code">{code}</code>}
          </span>
        ) : null}
      </span>
    </span>
  );
}
